import React, { useState, useEffect } from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import Button from '@/UIlibrary/Button/Button';
import { useNavigate, useLocation } from 'react-router-dom';
import { getLanguageFromPath } from '@/utils/utils';
import { useTranslations } from 'use-intl';
import { ArrowLeft } from 'react-feather';
import { useAgentStore } from '@/pages/CustomerSupport/useAgents';
import { useAgentToolsStore } from '@/pages/CustomerSupport/useAgentTools';
import { ToolInstructionBuilder } from '@/pages/ManageAgents/components/ToolInstructionBuilder';
import { ConfigurationTab } from '@/pages/ManageAgents/components/ConfigurationTab';
import { BehaviorMessagesTab } from '@/pages/ManageAgents/components/BehaviorMessagesTab';
import { KnowledgeBaseTab } from '@/pages/ManageAgents/components/KnowledgeBaseTab';
import { ToolInstructionWithTools, InstructionStep } from '@/pages/CustomerSupport/types';
import { TabItem, TabMenu } from '@/UIlibrary/TabMenu/TabMenu';
import { toast } from 'react-hot-toast';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const supabase = createClient(supabaseUrl, supabaseKey);

export const TAB_CONFIGURATION = 'configuration';
export const TAB_BEHAVIOR_MESSAGES = 'behaviorMessages';
export const TAB_KNOWLEDGE_BASE = 'knowledgeBase';
export type TAB_KEYS =
	| typeof TAB_CONFIGURATION
	| typeof TAB_BEHAVIOR_MESSAGES
	| typeof TAB_KNOWLEDGE_BASE;

const ManageAgentsDetails: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const language = getLanguageFromPath(window.location.pathname);
	const t = useTranslations('ManageAgents');
	const { selectedAgent, updateAgent, fetchKnowledgeBase, saveKnowledgeBase, resetError } =
		useAgentStore();
	const {
		contextVariables,
		loadContextVariables,
		tools,
		instructions,
		loadAgentTools,
		loadToolInstructions,
		saveToolInstruction,
		deleteToolInstruction,
		parseStepsFromInstruction,
		error: storeError,
		isLoading: isLoadingTools
	} = useAgentToolsStore();

	const [model, setModel] = useState(selectedAgent?.model || '');
	const [systemPrompt, setSystemPrompt] = useState(selectedAgent?.system_prompt || '');
	const [transitionMessage, setTransitionMessage] = useState(
		selectedAgent?.transition_message || ''
	);
	const [socialNetworkAnalysisFailureMessage, setSocialNetworkAnalysisFailureMessage] = useState(
		selectedAgent?.social_network_analysis_failure_message || ''
	);
	const [markdown, setMarkdown] = useState('');
	const [isSaving, setIsSaving] = useState(false);
	const [isFetching, setIsFetching] = useState(false);
	const [activeTab, setActiveTab] = useState(0);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isVariablesOpen, setIsVariablesOpen] = useState(false);
	const [isToolInstructionsExpanded, setIsToolInstructionsExpanded] = useState(true);
	const [showInstructionBuilder, setShowInstructionBuilder] = useState(false);
	const [editingInstruction, setEditingInstruction] = useState<ToolInstructionWithTools | null>(
		null
	);

	useEffect(() => {
		if (selectedAgent) {
			setModel(selectedAgent.model);
			setSystemPrompt(selectedAgent.system_prompt || '');
			setTransitionMessage(selectedAgent.transition_message || '');
			setSocialNetworkAnalysisFailureMessage(
				selectedAgent.social_network_analysis_failure_message || ''
			);
			loadContextVariables();
			loadAgentTools();
			loadToolInstructions(selectedAgent.id);
		}
	}, [selectedAgent]);

	const handleSave = async () => {
		if (!selectedAgent) return;

		setIsSaving(true);
		const success = await updateAgent(selectedAgent.id, {
			model,
			system_prompt: systemPrompt,
			transition_message: transitionMessage || null,
			social_network_analysis_failure_message:
				selectedAgent.agent_type === 'social_network'
					? socialNetworkAnalysisFailureMessage || null
					: undefined
		});
		setIsSaving(false);

		if (success) {
			toast.success(t('AgentUpdatedSuccessfully'));
		}
	};

	const hasChanges =
		selectedAgent &&
		(model !== selectedAgent.model ||
			systemPrompt !== (selectedAgent.system_prompt || '') ||
			transitionMessage !== (selectedAgent.transition_message || '') ||
			(selectedAgent.agent_type === 'social_network' &&
				socialNetworkAnalysisFailureMessage !==
					(selectedAgent.social_network_analysis_failure_message || '')));

	const loadKnowledgeBase = async () => {
		if (!selectedAgent) return;

		setIsFetching(true);
		resetError();

		const knowledgeBase = await fetchKnowledgeBase(selectedAgent.id);

		if (knowledgeBase) {
			setMarkdown(knowledgeBase.markdown_content_raw || '');
		} else {
			setMarkdown('');
		}

		setIsFetching(false);
	};

	const handleSaveKnowledgeBase = async () => {
		if (!selectedAgent || !markdown.trim()) {
			toast.error(t('EnterMarkdownContent'));
			return;
		}

		setIsSaving(true);
		resetError();

		try {
			const { error, data } = await supabase.functions.invoke('pinecone-create-embeddings', {
				body: {
					markdown: markdown,
					organizationId: selectedAgent.organization_id,
					agentType: selectedAgent.agent_type
				}
			});

			if (error) {
				toast.error(error.message || t('FailedToCreateEmbeddings'));
				setIsSaving(false);
				return;
			}

			const saveResult = await saveKnowledgeBase(selectedAgent.id, markdown, data?.namespace);

			if (!saveResult) {
				toast.error(t('FailedToSaveKnowledgeBase'));
				setIsSaving(false);
				return;
			}

			toast.success(
				`${t('KnowledgeBaseUpdatedSuccessfully')} ${data?.totalChunks} ${t('ChunksCreated')} ${t('And')} ${data?.totalUpserted} ${t('VectorsUpserted')}.`
			);
			handleCloseModal();
		} catch (error) {
			console.error('Error processing knowledge base:', error);
			toast.error(t('UnexpectedError'));
		} finally {
			setIsSaving(false);
		}
	};

	const handleOpenModal = () => {
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
	};

	const handleEditInstruction = (instruction: ToolInstructionWithTools) => {
		setEditingInstruction(instruction);
		setShowInstructionBuilder(true);
	};

	const handleDeleteInstruction = async (id: string) => {
		if (window.confirm(t('ConfirmDeleteInstruction'))) {
			const success = await deleteToolInstruction(id);
			if (success && selectedAgent) {
				await loadToolInstructions(selectedAgent.id);
				toast.success(t('InstructionDeletedSuccessfully'));
			}
		}
	};

	const handleSaveInstruction = async (params: { steps: InstructionStep[] }) => {
		if (!selectedAgent) return;

		const success = await saveToolInstruction({
			id: editingInstruction?.id,
			agentId: selectedAgent.id,
			steps: params.steps,
			enabled: true,
			sortOrder: editingInstruction?.sort_order ?? instructions.length
		});

		if (success) {
			await loadToolInstructions(selectedAgent.id);
			setShowInstructionBuilder(false);
			setEditingInstruction(null);
			toast.success(t('InstructionSavedSuccessfully'));
		}
	};

	const parsedInitialData = editingInstruction
		? {
				id: editingInstruction.id,
				steps: parseStepsFromInstruction(editingInstruction)
			}
		: undefined;

	const renderInstructionBuilder = () => {
		if (!showInstructionBuilder) return null;

		return (
			<ToolInstructionBuilder
				isOpen={showInstructionBuilder}
				onClose={() => {
					setShowInstructionBuilder(false);
					setEditingInstruction(null);
				}}
				availableTools={tools}
				contextVariables={contextVariables}
				onSave={handleSaveInstruction}
				initialData={parsedInitialData}
			/>
		);
	};

	const tabs: TabItem[] = [
		{
			title: t('Configuration'),
			content: (
				<ConfigurationTab
					agentId={selectedAgent?.id ?? ''}
					model={model}
					systemPrompt={systemPrompt}
					isSaving={isSaving}
					hasChanges={!!hasChanges}
					contextVariables={contextVariables}
					instructions={instructions}
					isLoadingTools={isLoadingTools}
					storeError={storeError}
					isVariablesOpen={isVariablesOpen}
					isToolInstructionsExpanded={isToolInstructionsExpanded}
					onModelChange={setModel}
					onSystemPromptChange={setSystemPrompt}
					onSave={handleSave}
					onToggleVariables={() => setIsVariablesOpen(!isVariablesOpen)}
					onToggleInstructions={() => setIsToolInstructionsExpanded(!isToolInstructionsExpanded)}
					onAddInstruction={() => setShowInstructionBuilder(true)}
					onEditInstruction={handleEditInstruction}
					onDeleteInstruction={handleDeleteInstruction}
				/>
			),
			badgeVisible: false
		},
		{
			title: t('BehaviorMessages'),
			content: (
				<BehaviorMessagesTab
					agentType={selectedAgent?.agent_type ?? ''}
					transitionMessage={transitionMessage}
					transitionMessageWhereItHappens={
						selectedAgent?.transition_message_where_it_happens ?? null
					}
					socialNetworkAnalysisFailureMessage={socialNetworkAnalysisFailureMessage}
					isSaving={isSaving}
					hasChanges={!!hasChanges}
					onTransitionMessageChange={setTransitionMessage}
					onSocialNetworkAnalysisFailureMessageChange={setSocialNetworkAnalysisFailureMessage}
					onSave={handleSave}
				/>
			),
			badgeVisible: false
		},
		{
			title: t('KnowledgeBase'),
			content: (
				<KnowledgeBaseTab
					markdown={markdown}
					isSaving={isSaving}
					isFetching={isFetching}
					onMarkdownChange={setMarkdown}
					onSave={handleOpenModal}
				/>
			),
			badgeVisible: false
		}
	];

	const getStartingTab = (index: number): TAB_KEYS => {
		switch (index) {
			case 0:
				return TAB_CONFIGURATION;
			case 1:
				return TAB_BEHAVIOR_MESSAGES;
			case 2:
				return TAB_KNOWLEDGE_BASE;
			default:
				return TAB_CONFIGURATION;
		}
	};

	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.has('startingTab')) {
			const currentTab = urlParams.get('startingTab') as TAB_KEYS;
			const tabIndex =
				currentTab === 'knowledgeBase' ? 2 : currentTab === 'behaviorMessages' ? 1 : 0;
			setActiveTab(tabIndex);
		}
	}, []);

	const handleTabChange = (index: number) => {
		setActiveTab(index);
		const params = new URLSearchParams();
		params.set('startingTab', getStartingTab(index));
		navigate(`${location.pathname}?${params.toString()}`);
	};

	useEffect(() => {
		if (activeTab === 2) {
			loadKnowledgeBase();
		}
	}, [activeTab]);

	return (
		<main className="p-3 md:p-8">
			<div className="flex flex-col gap-4">
				<div>
					<Button
						onClick={() => navigate(`/${language}/center/manage-agents`)}
						color="primary"
						size="medium"
						textOnly
						label={t('Back')}
						startIcon={<ArrowLeft />}
					/>
				</div>
				<Typography type="display">
					{t('AgentDetailsTitle')}: {selectedAgent?.agent_type}
				</Typography>

				<TabMenu
					activeTabIndex={activeTab}
					tabs={tabs}
					maxWidth={1500}
					onTabChanged={handleTabChange}
				/>
			</div>

			<ConfirmModal
				isOpen={isModalOpen}
				onClose={handleCloseModal}
				onCancel={handleCloseModal}
				onConfirm={handleSaveKnowledgeBase}
				title={t('ConfirmSaveKnowledgeBase')}
				subtitle={t('ConfirmSaveKnowledgeBaseSubtitle')}
				confirmActionText={t('Save')}
				type="confirm"
				confirmBtnLoading={isSaving}
			/>
			{renderInstructionBuilder()}
		</main>
	);
};

export default ManageAgentsDetails;
